import React, { useEffect, useState } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import {
  Flex,
  Box,
  Text,
  Badge,
} from "@chakra-ui/react";
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import { useDispatch, useSelector } from "react-redux";
import { fetchAllNotificationsAction } from "state/actions/notificationActions";
import { EllipsisVertical, Plus, Telescope } from "lucide-react";
import ThreeDotMenu from "components/Menus/ThreeDotMenu.js";
import { truncateText } from "./utils/truncateText";
import GradientButton from "components/Buttons/GradientButton";
import ReusableDrawer from "components/Drawers/ReusableDrawer";
import AddNotificationDrawer from "./components/AddNotificationForm";
import ViewNotification from "./components/ViewNotification";

const NotificationManagement = () => {
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isViewNotificationDrawerOpen, setViewNotificationDrawerOpen] = useState(false);
  const dispatch = useDispatch();
  const allNotifications = useSelector(state => state.notifications.allNotifications);
  const loading = useSelector(state => state.notifications.loading);
  const error = useSelector(state => state.notifications.error);

  const [isAddNotificationOpen, setAddNotificationOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    targetUsers: [],
    notificationType: "",
    notificationSubtype: "",
    message: "",
    sendOption: "now",
    scheduleDate: "",
  });

    useEffect(() => {
    dispatch(fetchAllNotificationsAction());
  }, [dispatch]);

  const handleAddNotificationOpen = () => setAddNotificationOpen(true);

  const handleAddNotificationClose = () => {
    setAddNotificationOpen(false);
    // Reset form data when closing
    setFormData({
      title: "",
      targetUsers: [],
      notificationType: "",
      notificationSubtype: "",
      message: "",
      sendOption: "now",
      scheduleDate: "",
    });
  };

  const handleAddNotificationSubmit = (data) => {
    // Logic to save the notification data (API call or local state update)
    console.log(data);
    handleAddNotificationClose();
  };



  const handleViewNotificationDetails = (notification) => {
    setSelectedNotification(notification);
    setViewNotificationDrawerOpen(true);
  };

  const handleCloseViewNotificationDrawer = () => {
    setSelectedNotification(null);
    setViewNotificationDrawerOpen(false);
  };

  console.log("All notifications from Redux:", allNotifications);

  const filteredNotifications = allNotifications.filter(
    notif => notif.notification_user_types && notif.notification_user_types.length > 0
  );

  const notificationData = filteredNotifications.map(notif => ({
    notificationId: notif.notification_id,
    userId: notif.user_id || "Admin",
    message: notif.message,
    notificationType: notif.notification_type,
    targetUsers: notif.notification_user_types.map(t => t.user_type),
    status: notif.read_status === "Read" ? "Read" : "Sent",
    sentAt: new Date(notif.sent_at).toLocaleString(),
    scheduledDate: "",
  }));


  const formattedUsers = selectedNotification?.targetUsers
  .map(user => user + 's')
  .join(', ');

  const columnsData = [
    {
      Header: "Sender",
      accessor: "userId",
    },
    {
      Header: "Message",
      accessor: "message",
      Cell: ({ value }) => (
        <Text>{truncateText(value)}</Text>
      ),
    },
    {
      Header: "Type",
      accessor: "notificationType",
    },
    {
      Header: "Target Users",
      accessor: "targetUsers",
      Cell: ({ value }) => (
        <Text>{truncateText(value.join(", "), 5)}</Text>
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Badge
          colorScheme={
            value === "Sent" ? "green" :
              value === "Pending" ? "orange" : "gray"
          }
          borderRadius="full"
          px={2}
        >
          {value}
        </Badge>
      ),
    },
    {
      Header: "Sent At",
      accessor: "sentAt",
    },
    // {
    //   Header: "Scheduled Date",
    //   accessor: "scheduledDate",
    // },
    {
      Header: "Actions",
      accessor: "moreOptions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={[
            {
              icon: <Telescope size={16} />,
              text: 'View Notification',
              onClick: () => handleViewNotificationDetails(row.original),
            }
          ]}
        />
      ),
    },
  ];

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>

      <Box p={6}>
        <Flex justifyContent="space-between" alignItems="center">
          {loading ? (
            <Skeleton height="20px" width="160px" />
          ) : (
            <Text fontSize="md" fontWeight="bold">
              All Notifications: {notificationData.length}
            </Text>
          )}

          {loading ? (
            <Skeleton height="40px" width="150px" style={{ marginRight: "20px" }} />
          ) : (
            <GradientButton
              icon={<Plus size={16} />}
              onClick={handleAddNotificationOpen}
              height="40px"
              mr={5}
            >
              Add Notification
            </GradientButton>
          )}
        </Flex>
      </Box>
      <ReusableTable columnsData={columnsData} tableData={notificationData} isLoading={loading} />

      <ReusableDrawer
        isOpen={isAddNotificationOpen}
        onClose={handleAddNotificationClose}
        headerContent={<h2 style={{ fontSize: "md" }}>Add Notification</h2>}
        bodyContent={
          <AddNotificationDrawer
            formData={formData}
            setFormData={setFormData}
            onClose={handleAddNotificationClose}
          />
        }
        size="md"
      />

      {/* View Notification Drawer */}
      <ReusableDrawer
        isOpen={isViewNotificationDrawerOpen}
        onClose={handleCloseViewNotificationDrawer}
        headerContent={
          selectedNotification && (
            <h2 style={{ fontSize: 'md' }}>
              {`View Notification - to ${formattedUsers}`}
            </h2>
          )
        }
        bodyContent={
          selectedNotification && (
            <ViewNotification notification={selectedNotification} onClose={handleCloseViewNotificationDrawer} />
          )
        }
        size="lg"
      />

      {/* Manage Notification Drawer
      <ReusableDrawer
        isOpen={isManageNotificationDrawerOpen}
        onClose={handleCloseManageNotificationDrawer}
        headerContent={
          selectedNotification && (
            <h2 style={{ fontSize: 'md' }}>
              Manage Notification - #{selectedNotification.notificationId}
            </h2>
          )
        }
        bodyContent={
          selectedNotification && (
            <ManageNotification
              notification={selectedNotification}
              onClose={handleCloseManageNotificationDrawer}
            />
          )
        }
        size="lg"
      /> */}
    </Flex>
  );
};

export default NotificationManagement;
